const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    'Content-Type': 'application/json'
  };
}

async function rpc(name, body, label) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${name}`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(body)
    }
  );

  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}

function deviceIdentity(row) {
  const platform =
    String(
      row?.platform || ''
    )
      .trim()
      .toLowerCase();

  const name =
    String(
      row?.device_name || ''
    )
      .trim()
      .toLowerCase();

  /*
   * The existing RPC exposes no persistent device/install ID.
   * Use platform + generated device name when available.
   *
   * Rows without a device name remain distinct rather than
   * accidentally merging every unnamed phone on a platform.
   */
  return name
    ? `${platform}|${name}`
    : `session:${row?.id || ''}`;
}


function laterTimestamp(
  first,
  second
) {
  const firstTime =
    Date.parse(
      first || ''
    ) || 0;

  const secondTime =
    Date.parse(
      second || ''
    ) || 0;

  return secondTime > firstTime
    ? second
    : first;
}


function collapseDeviceSessions(
  rows
) {
  const grouped =
    new Map();

  (
    Array.isArray(rows)
      ? rows
      : []
  ).forEach(
    row => {
      const key =
        deviceIdentity(
          row
        );

      const current =
        grouped.get(
          key
        );

      if (!current) {
        grouped.set(
          key,
          {
            ...row,

            device_key:
              key,

            session_ids:
              row?.id
                ? [row.id]
                : []
          }
        );

        return;
      }


      const rowIsNewer =
        (
          Date.parse(
            row?.created_at ||
            ''
          ) || 0
        )
        >
        (
          Date.parse(
            current?.created_at ||
            ''
          ) || 0
        );


      const newest =
        rowIsNewer
          ? row
          : current;

      const older =
        rowIsNewer
          ? current
          : row;


      grouped.set(
        key,
        {
          ...older,
          ...newest,

          id:
            newest.id,

          device_key:
            key,

          session_ids:
            Array.from(
              new Set([
                ...(
                  current
                    .session_ids ||
                  []
                ),

                ...(
                  row?.id
                    ? [row.id]
                    : []
                )
              ])
            ),

          /*
           * "Signed in" now means the most recent login
           * represented by this physical-device card.
           */
          created_at:
            laterTimestamp(
              current.created_at,
              row.created_at
            ),

          last_used_at:
            laterTimestamp(
              current.last_used_at,
              row.last_used_at
            )
        }
      );
    }
  );


  return Array
    .from(
      grouped.values()
    )
    .sort(
      (a, b) =>
        (
          Date.parse(
            b?.last_used_at ||
            b?.created_at ||
            ''
          ) || 0
        )
        -
        (
          Date.parse(
            a?.last_used_at ||
            a?.created_at ||
            ''
          ) || 0
        )
    );
}


export async function listStudentDevices({
  studentId,
  password = null,
  token = null
}) {
  const result = await rpc(
    'student_list_devices',
    {
      p_student_id: studentId,
      p_password: password,
      p_token: token
    },
    'Loading devices'
  );

  if (!result?.ok) {
    if (result?.status === 'not_authorised') {
      throw new Error('Incorrect password.');
    }

    throw new Error('Devices could not be loaded.');
  }

  return collapseDeviceSessions(result.devices);
}

export async function revokeStudentDevice({
  studentId,
  deviceSessionId,
  deviceSessionIds = null,
  password = null,
  token = null
}) {
  const ids =
    Array.from(
      new Set(
        (
          Array.isArray(
            deviceSessionIds
          )
          &&
          deviceSessionIds.length
            ? deviceSessionIds
            : [deviceSessionId]
        ).filter(Boolean)
      )
    );


  for (
    const id
    of ids
  ) {
    const result =
      await rpc(
        'student_revoke_device',
        {
          p_student_id:
            studentId,

          p_device_session_id:
            id,

          p_password:
            password,

          p_token:
            token
        },
        'Signing out device'
      );


    if (!result?.ok) {
      /*
       * One of several old duplicate sessions may already
       * have disappeared. That does not make the overall
       * device sign-out fail.
       */
      if (
        result?.status ===
          'device_not_found'
      ) {
        continue;
      }


      if (
        result?.status ===
          'not_authorised'
      ) {
        throw new Error(
          'You are no longer authorised to manage these devices.'
        );
      }


      throw new Error(
        'The device could not be signed out.'
      );
    }
  }


  return {
    ok:
      true,

    revoked:
      ids.length
  };
}
